'use client';

import LocaleSwitcher from '@/components/LocaleSwitcher';
import { useFooterNavLinks } from '@/components/layout/footerNavLinks';
import { ThemeSwitcher } from '@/components/theme/ThemeSwitcher';
import { Button } from '@/components/ui/button';
import { useCalendlyPopup } from '@/hooks/useCalendlyPopup';
import { Link, usePathname } from '@/i18n/routing';
import { useAppDispatch } from '@/lib/redux/hooks';
import { setCustomizeSheetOpen } from '@/lib/redux/slices/uiSettingsSlice';
import { CalendarDays, Sparkles } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useRouter, useSearchParams } from 'next/navigation';
import { usePostHog } from 'posthog-js/react';
import * as CookieConsent from 'vanilla-cookieconsent';

interface SidebarMenuContentProps {
  /** Show cookie preferences button (true for authenticated users) */
  showCookiePreferences?: boolean;
  /** Show the "Prispôsobenie" row that opens the in-shell Customize sheet.
   *  Only true in app-shell surfaces where the sheet is mounted. */
  showCustomize?: boolean;
  /** Callback when items are clicked (for Sheet closing) */
  onItemClick?: () => void;
  /**
   * Height of each menu item row. Default h-11 (44px) meets WCAG 2.1 AA SC 2.5.5.
   * h-10 (40px) is used in Sheet contexts where surrounding affordances compensate.
   * h-8 (32px) is retained for compact/embedded layouts but falls below WCAG minimum.
   */
  itemHeight?: 'h-8' | 'h-10' | 'h-11';
  /** Theme/locale switcher row at the top (hidden where the host surface already shows them). */
  showSwitchers?: boolean;
  /** Calendly "schedule a call" row (sidebar surfaces only). */
  showScheduleCall?: boolean;
  /** Prepend the "App" (/chat) link — marketing surfaces only. */
  includeAppLink?: boolean;
  /** Include the advocate landing page link once it is launched (#7951). */
  includeAdvocateLanding?: boolean;
}

/**
 * Shared menu content for the footer/hamburger menus (sidebar footer popover,
 * SidebarAnonFooter, and both headers' HamburgerMenu). Contains: Theme/Locale
 * switchers, footer links, feedback, cookie preferences, legal links — all
 * link sets come from the shared `useFooterNavLinks` config so the surfaces
 * can't drift.
 */
export const SidebarMenuContent = ({
  showCookiePreferences = false,
  showCustomize = false,
  onItemClick,
  itemHeight = 'h-11',
  showSwitchers = true,
  showScheduleCall = true,
  includeAppLink = false,
  includeAdvocateLanding = false,
}: SidebarMenuContentProps) => {
  const tFooter = useTranslations('Footer');
  const tSidebar = useTranslations('SidebarHeader');
  const dispatch = useAppDispatch();
  const posthog = usePostHog();
  const { openCalendly, isLoading: isCalendlyLoading } = useCalendlyPopup();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { footerLinks, legalLinks } = useFooterNavLinks({
    includeAppLink,
    includeAdvocateLanding,
  });

  const handleShowCookiePreferences = () => {
    onItemClick?.();
    CookieConsent.showPreferences();
  };

  const openFeedbackModal = () => {
    onItemClick?.();
    const current = new URLSearchParams(Array.from(searchParams.entries()));
    current.set('show-feedback', 'true');
    const search = current.toString();
    const query = search ? `?${search}` : '';
    router.push(`${pathname}${query}`);
  };

  const handleOpenCustomize = () => {
    onItemClick?.();
    dispatch(setCustomizeSheetOpen(true));
  };

  return (
    <div className='flex flex-col space-y-1 max-h-[var(--radix-popper-available-height,calc(100vh_-_8rem))] overflow-y-auto'>
      {/* Theme and Language Switchers */}
      {showSwitchers && (
        <div className='flex items-center justify-center gap-2 pb-2 mb-1 border-b border-line-default'>
          <ThemeSwitcher />
          <LocaleSwitcher />
        </div>
      )}

      {/* Customize — shown only in app-shell surfaces where the
          CustomizeSheet is mounted. */}
      {showCustomize && (
        <Button
          variant='ghost'
          className={`w-full justify-start text-sm font-normal ${itemHeight} px-3 text-brand-tertiary/70 hover:text-brand-tertiary`}
          onClick={handleOpenCustomize}
        >
          <Sparkles className='mr-2 h-4 w-4' />
          {tSidebar('customize')}
        </Button>
      )}

      {/* Main menu items */}
      {footerLinks.map((link) => (
        <Button
          key={link.href}
          variant='ghost'
          className={`w-full justify-start text-sm font-normal ${itemHeight} px-3`}
          asChild={!link.isExternal}
          onClick={onItemClick}
        >
          {link.isExternal ? (
            <a href={link.href} target='_blank' rel='noopener noreferrer'>
              {link.label}
            </a>
          ) : (
            <Link href={link.href}>{link.label}</Link>
          )}
        </Button>
      ))}

      {/* Feedback */}
      <Button
        variant='ghost'
        className={`w-full justify-start text-sm font-normal ${itemHeight} px-3`}
        onClick={openFeedbackModal}
      >
        {tFooter('feedback')}
      </Button>

      {/* Schedule a Meeting */}
      {showScheduleCall && (
        <Button
          variant='ghost'
          className={`w-full justify-start text-sm font-normal ${itemHeight} px-3 text-brand-tertiary/70 hover:text-brand-tertiary disabled:cursor-wait`}
          aria-busy={isCalendlyLoading}
          disabled={isCalendlyLoading}
          onClick={() => {
            onItemClick?.();
            posthog?.capture('calendly_link_clicked', {
              source: 'sidebar_menu',
            });
            openCalendly();
          }}
        >
          <CalendarDays className='mr-2 h-4 w-4' />
          {tFooter('scheduleCall')}
        </Button>
      )}

      {/* Cookie Preferences - only for authenticated users */}
      {showCookiePreferences && (
        <Button
          variant='ghost'
          className={`w-full justify-start text-sm font-normal ${itemHeight} px-3`}
          onClick={handleShowCookiePreferences}
        >
          {tFooter('cookies')}
        </Button>
      )}

      {/* Legal links */}
      {legalLinks.map((link) => (
        <Button
          key={link.href}
          variant='ghost'
          className={`w-full justify-start text-sm font-normal ${itemHeight} px-3`}
          asChild
          onClick={onItemClick}
        >
          <Link href={link.href}>{link.label}</Link>
        </Button>
      ))}
    </div>
  );
};
